/**
 * Logistics + Capacity + Amenities edit sections for the venue detail page.
 * Logistics edits each structured field's text while preserving its
 * teacher-visibility flag; Capacity edits the numeric timings (FareHarbor
 * location ID is shown read-only — it's managed by the FH sync, not patchable).
 */
import { useState } from 'react';

import type {
  VenueDetail,
  VenueLogistics,
  VenueLogisticsKey,
  VenuePatch,
} from '../../../../api/venues';
import { Field } from '../venueCreateFields';

import { FhBadge, TeacherEye } from './detailBadges';
import { EditSection } from './EditSection';
import { ReadGrid, type ReadRow } from './ReadGrid';
import { Input, Textarea, useFormDraft } from './sectionHelpers';

const nullable = (s: string) => (s.trim().length === 0 ? null : s.trim());

interface SectionProps {
  venue: VenueDetail;
  readOnly: boolean;
  save: (patch: VenuePatch) => Promise<void>;
}

const LOGISTICS: { key: VenueLogisticsKey; label: string; area: boolean; teacher: boolean }[] = [
  { key: 'parking', label: 'Parking info', area: true, teacher: true },
  { key: 'loadIn', label: 'Load-in notes', area: true, teacher: true },
  { key: 'powerAv', label: 'Power / AV', area: true, teacher: true },
  { key: 'space', label: 'Room / space details', area: true, teacher: true },
  { key: 'onDuty', label: 'On-duty contact note', area: true, teacher: true },
  { key: 'leadTime', label: 'Booking lead time', area: false, teacher: false },
  { key: 'weeknights', label: 'Available weeknights', area: false, teacher: false },
  { key: 'blackout', label: 'Blackout windows', area: false, teacher: false },
];

const teacherOf = (venue: VenueDetail, f: (typeof LOGISTICS)[number]) =>
  venue.logistics?.[f.key]?.teacherVisible ?? f.teacher;

export function LogisticsSection({ venue, readOnly, save }: SectionProps) {
  const seed = Object.fromEntries(
    LOGISTICS.map((f) => [f.key, venue.logistics?.[f.key]?.value ?? '']),
  ) as Record<VenueLogisticsKey, string>;
  const { draft, setField, reset } = useFormDraft(seed, venue.updatedAt);

  const onSave = () => {
    const next: VenueLogistics = {};
    for (const f of LOGISTICS) {
      const value = draft[f.key].trim();
      if (value) next[f.key] = { value, teacherVisible: teacherOf(venue, f) };
    }
    return save({ logistics: Object.keys(next).length > 0 ? next : null });
  };

  return (
    <EditSection
      title="Logistics"
      hint="teachers read the marked fields day-of"
      readOnly={readOnly}
      onCancel={reset}
      onSave={onSave}
      view={
        <ReadGrid
          cols={1}
          rows={LOGISTICS.map(
            (f): ReadRow => ({
              label: f.label,
              value: venue.logistics?.[f.key]?.value,
              teacher: teacherOf(venue, f),
              full: true,
            }),
          )}
        />
      }
      form={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {LOGISTICS.map((f) => (
            <Field key={f.key} label={f.label} hint={<TeacherEye on={teacherOf(venue, f)} />}>
              {f.area ? (
                <Textarea rows={1} value={draft[f.key]} onChange={(v) => setField(f.key, v)} />
              ) : (
                <Input
                  value={draft[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  style={{ maxWidth: 320 }}
                />
              )}
            </Field>
          ))}
        </div>
      }
    />
  );
}

const toInt = (s: string, fallback: number) => {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : fallback;
};

export function CapacitySection({ venue, readOnly, save }: SectionProps) {
  const seed = {
    maxCapacity: String(venue.maxCapacity),
    setup: String(venue.setupTimeMinutes),
    cleanup: String(venue.cleanupTimeMinutes),
  };
  const { draft, setField, reset } = useFormDraft(seed, venue.updatedAt);

  return (
    <EditSection
      title="Capacity & timing"
      action={<FhBadge />}
      readOnly={readOnly}
      onCancel={reset}
      onSave={() =>
        save({
          maxCapacity: toInt(draft.maxCapacity, venue.maxCapacity),
          setupTimeMinutes: toInt(draft.setup, venue.setupTimeMinutes),
          cleanupTimeMinutes: toInt(draft.cleanup, venue.cleanupTimeMinutes),
        })
      }
      view={
        <ReadGrid
          cols={3}
          rows={[
            { label: 'Max capacity', value: venue.maxCapacity, fh: true, mono: true },
            { label: 'Setup', value: `${venue.setupTimeMinutes} min`, mono: true },
            { label: 'Cleanup', value: `${venue.cleanupTimeMinutes} min`, mono: true },
            {
              label: 'FareHarbor location ID',
              value: venue.fareharborLocationId,
              mono: true,
              full: true,
            },
          ]}
        />
      }
      form={
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
          <Field label="Max capacity" required>
            <Input mono value={draft.maxCapacity} onChange={(v) => setField('maxCapacity', v)} />
          </Field>
          <Field label="Setup (min)">
            <Input mono value={draft.setup} onChange={(v) => setField('setup', v)} />
          </Field>
          <Field label="Cleanup (min)">
            <Input mono value={draft.cleanup} onChange={(v) => setField('cleanup', v)} />
          </Field>
        </div>
      }
    />
  );
}

function AmenitiesView({ amenities, notes }: { amenities: string[]; notes: string | null }) {
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {amenities.length === 0 && (
          <span style={{ fontSize: 12.5, color: 'var(--nb-faint)', fontStyle: 'italic' }}>
            None listed
          </span>
        )}
        {amenities.map((a) => (
          <span key={a} className="nb-pill" style={{ height: 22, fontSize: 11 }}>
            {a}
          </span>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <div className="nb-eyebrow" style={{ marginBottom: 4 }}>
          Staff notes
        </div>
        <ReadGrid rows={[{ label: 'Notes', value: notes, full: true }]} />
      </div>
    </div>
  );
}

function AmenityChips({
  amenities,
  pending,
  setPending,
  onAdd,
  onRemove,
}: {
  amenities: string[];
  pending: string;
  setPending: (v: string) => void;
  onAdd: () => void;
  onRemove: (a: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
      {amenities.map((a) => (
        <span key={a} className="nb-pill" style={{ height: 22, fontSize: 11 }}>
          {a}
          <button
            type="button"
            onClick={() => onRemove(a)}
            aria-label={`Remove ${a}`}
            style={{
              border: 0,
              background: 'transparent',
              color: 'var(--nb-faint)',
              cursor: 'pointer',
              marginLeft: 4,
            }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onAdd();
          }
        }}
        placeholder="Add amenity + Enter"
        style={{
          height: 22,
          fontSize: 11,
          padding: '0 8px',
          borderRadius: 999,
          border: '1px solid var(--nb-border-2)',
          background: 'var(--nb-bg)',
          color: 'var(--nb-ink)',
          outline: 'none',
        }}
      />
    </div>
  );
}

export function AmenitiesSection({ venue, readOnly, save }: SectionProps) {
  const seed = { amenities: venue.amenities, notes: venue.venueNotes ?? '' };
  const { draft, setDraft, setField, reset } = useFormDraft(seed, venue.updatedAt);
  const [pending, setPending] = useState('');

  const addAmenity = () => {
    const a = pending.trim();
    if (a.length === 0 || draft.amenities.includes(a)) return;
    setDraft((d) => ({ ...d, amenities: [...d.amenities, a] }));
    setPending('');
  };
  const removeAmenity = (a: string) =>
    setDraft((d) => ({ ...d, amenities: d.amenities.filter((x) => x !== a) }));

  return (
    <EditSection
      title="Amenities & notes"
      hint="internal"
      readOnly={readOnly}
      onCancel={() => {
        reset();
        setPending('');
      }}
      onSave={() => save({ amenities: draft.amenities, venueNotes: nullable(draft.notes) })}
      view={<AmenitiesView amenities={venue.amenities} notes={venue.venueNotes} />}
      form={
        <div>
          <AmenityChips
            amenities={draft.amenities}
            pending={pending}
            setPending={setPending}
            onAdd={addAmenity}
            onRemove={removeAmenity}
          />
          <Field label="Staff notes">
            <Textarea value={draft.notes} onChange={(v) => setField('notes', v)} />
          </Field>
        </div>
      }
    />
  );
}
